/**
 * Error Boundary Component
 * 
 * Catches React errors in child components and displays a fallback UI.
 * Prevents the entire app from crashing when an MFE fails.
 * 
 * @module components/common/ErrorBoundary
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@sequelae/lore-core';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🛑 [ErrorBoundary] Caught error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h2>Something went wrong</h2>
            <p className="error-boundary-message">
              This feature is currently experiencing issues. This may be due to:
            </p>
            <ul className="error-boundary-reasons">
              <li>Network connectivity problems</li>
              <li>Temporary service outage</li>
              <li>Missing resources or assets</li>
            </ul>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development Only)</summary>
                <pre className="error-boundary-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="error-boundary-actions">
              <Button
                variant="primary"
                size="medium"
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              <Button
                variant="secondary"
                size="medium"
                onClick={() => window.location.href = '/rewards'}
              >
                Go to Rewards
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

